'use client';

import Script from 'next/script';
import { useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';

const GTM_ID = process.env.NEXT_PUBLIC_GA_TRACKING_ID ?? '';
export const consentCookieName = '__hs_opt_out';

const clickEventHandler = (e: MouseEvent) => {
	const target: HTMLElement | null = e.target as HTMLElement;

	if (target.id === 'hs-eu-confirmation-button') {
		gtag('consent', 'update', {
			ad_storage: 'granted',
			analytics_storage: 'granted',
		});
		dataLayer.push({ event: 'ConsentUpdated' });

		document.body.removeEventListener('click', clickEventHandler);
	} else if (target.id === 'hs-eu-decline-button') {
		document.body.removeEventListener('click', clickEventHandler);
	}
};

const CookieBanner = () => {
	const [cookies] = useCookies([consentCookieName]);
	const [cookieAccepted, setCookieAccepted] = useState(false);

	useEffect(() => {
		// Check if user has already made a choice using HubSpot's cookie system
		const checkConsent = () => {
			const hasConsent =
				document.cookie.includes('hubspotutk') || document.cookie.includes('__hs_opt_out=no') || cookies.__hs_opt_out === 'no';

			setCookieAccepted(hasConsent);

			// Check if banner was already shown in the last 30 days
			const bannerShownData = localStorage.getItem('hubspot-banner-shown');
			const now = Date.now();
			const thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds

			if (bannerShownData) {
				const { timestamp } = JSON.parse(bannerShownData);
				const isExpired = now - timestamp > thirtyDaysInMs;

				if (isExpired) {
					// Remove expired data
					localStorage.removeItem('hubspot-banner-shown');
				}
			}

			const bannerShownRecently = localStorage.getItem('hubspot-banner-shown');

			if (!hasConsent && !bannerShownRecently) {
				// Mark banner as shown with timestamp
				localStorage.setItem(
					'hubspot-banner-shown',
					JSON.stringify({
						timestamp: now,
					}),
				);

				window._hsp = window._hsp || [];
				window._hsp.push(['showBanner']);

				document.body.addEventListener('click', clickEventHandler);
			}
		};

		checkConsent();
		const checkInterval = setInterval(checkConsent, 1000);

		return () => {
			clearInterval(checkInterval);
			document.body.removeEventListener('click', clickEventHandler);
		};
	}, [cookies.__hs_opt_out]);

	return (
		<>
			<>
				{/* Start of HubSpot Embed Code */}
				<Script
					id='hs-script-loader'
					strategy='afterInteractive'
					src={`//js.hs-scripts.com/${process.env.NEXT_PUBLIC_HUBSPOT_PORTAL_ID}.js`}
				/>
				{/* End of HubSpot Embed Code */}

				{/* Google Tag Manager */}
				<Script
					id='gtag'
					strategy='afterInteractive'
					dangerouslySetInnerHTML={{
						__html: `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}

        gtag('consent', 'default', {
          'ad_storage': ${cookieAccepted ? '"granted"' : '"denied"'},
          'analytics_storage': ${cookieAccepted ? '"granted"' : '"denied"'}
        });

        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${GTM_ID}');
        `,
					}}
				/>

				<noscript>
					<iframe
						src={`https://www.googletagmanager.com/ns.html?id=${GTM_ID}`}
						height='0'
						width='0'
						style={{ display: 'none', visibility: 'hidden' }}
					></iframe>
				</noscript>
				{/* End Google Tag Manager */}
			</>
		</>
	);
};

export default CookieBanner;

declare const gtag: Function;
declare const dataLayer: any[];
declare global {
	interface Window {
		_hsp: any[];
	}
}
